/**
 * Scroll-scrubbed video stack — text + media share the same scroll segments.
 * Pin distance is ~0.24× viewport per card so text scrub stays readable.
 * Avoids long tl.to({}, …) fillers that map most of the scroll to “nothing” (black void).
 */
(function () {
  "use strict";

  var stack = document.getElementById("smart-video-stack");
  if (!stack) return;
  if (typeof gsap === "undefined" || typeof ScrollTrigger === "undefined") return;

  gsap.registerPlugin(ScrollTrigger);

  var slides = gsap.utils.toArray(stack.querySelectorAll(".smart-video-slide"));
  if (!slides.length) return;

  var dotSpans = stack.querySelectorAll(".smart-video-stack__dots span");
  var reduced =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /** ~0.24× viewport per card — enough scroll for readable text scrub */
  function pinEndDistance() {
    var h = window.innerHeight || 800;
    var mult = window.innerWidth < 768 ? 0.21 : 0.24;
    return "+=" + Math.round(h * mult);
  }

  function setActiveDot(index) {
    if (!dotSpans || !dotSpans.length) return;
    for (var i = 0; i < dotSpans.length; i++) {
      dotSpans[i].classList.toggle("is-active", i === index);
    }
  }

  function tryPlay(video) {
    if (!video) return;
    var p = video.play();
    if (p && typeof p.catch === "function") p.catch(function () { });
  }

  /** Off-screen slides use preload="none"; start loading when the slide nears the viewport */
  function ensureVideoLoaded(vid, slide) {
    if (!vid) return;
    if (!vid.getAttribute("data-lazy-video")) {
      tryPlay(vid);
      return;
    }
    if (typeof IntersectionObserver === "undefined") {
      vid.preload = "auto";
      vid.load();
      tryPlay(vid);
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        vid.preload = "auto";
        vid.load();
        tryPlay(vid);
      },
      { rootMargin: "85% 0px", threshold: 0 }
    );
    io.observe(slide);
  }

  if (reduced) {
    slides.forEach(function (slide, index) {
      slide.style.zIndex = String(10 + index);
      var content = slide.querySelector(".smart-video-slide__content");
      var vid = slide.querySelector("video");
      if (content) gsap.set(content, { opacity: 1, y: 0 });
      ensureVideoLoaded(vid, slide);
      ScrollTrigger.create({
        trigger: slide,
        start: "top 75%",
        end: "bottom 25%",
        onEnter: function () {
          setActiveDot(index);
        },
        onEnterBack: function () {
          setActiveDot(index);
        },
      });
    });
    setActiveDot(0);
    return;
  }

  // Set the container stack to fixed full screen viewport height to contain absolute slides
  gsap.set(stack, {
    height: "100vh",
    width: "100%",
    position: "relative",
    overflow: "hidden"
  });

  slides.forEach(function (slide, index) {
    // Style slides absolutely so they sit on top of each other
    gsap.set(slide, {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      zIndex: 10 + index
    });

    var vid = slide.querySelector("video");
    ensureVideoLoaded(vid, slide);

    // For all slides except the first one, translate them off-screen down
    if (index > 0) {
      gsap.set(slide, { yPercent: 100 });
    }
  });

  // Create single scrub timeline that pins the stack container and slides each panel up one-by-one
  var tl = gsap.timeline({
    scrollTrigger: {
      trigger: stack,
      start: "top top",
      end: function () {
        return "+=" + (slides.length - 1) * window.innerHeight;
      },
      pin: true,
      pinSpacing: true,
      scrub: 1,
      invalidateOnRefresh: true,
      onUpdate: function (self) {
        var progress = self.progress;
        var activeIndex = Math.round(progress * (slides.length - 1));
        setActiveDot(activeIndex);
      }
    }
  });

  // Sequence the slides sliding up
  for (var i = 1; i < slides.length; i++) {
    tl.to(slides[i], {
      yPercent: 0,
      ease: "none"
    });
  }

  setActiveDot(0);
})();

/**
 * Ghost HTML blocks: `.smart-video-inner` + `.smart-video-panel` (no #smart-video-stack).
 * Scroll-scrub: heading fades up/out, caption + CTA fade in (Breakthrough Energy–style).
 */
(function () {
  "use strict";

  var inners = Array.prototype.slice
    .call(document.querySelectorAll(".smart-video-inner"))
    .filter(function (el) {
      return el.querySelector(".smart-video-panel");
    });

  if (!inners.length) return;

  if (typeof gsap === "undefined" || typeof ScrollTrigger === "undefined") {
    inners.forEach(function (inner) {
      inner.classList.add("smart-video-reveal--no-gsap");
    });
    return;
  }

  gsap.registerPlugin(ScrollTrigger);

  var reduced =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /** ~0.24× viewport per block — enough scroll for readable text scrub */
  function pinEndDistance() {
    var h = window.innerHeight || 800;
    var mult = window.innerWidth < 768 ? 0.21 : 0.24;
    return "+=" + Math.round(h * mult);
  }

  var EASE_IN = "power2.in";
  var EASE_OUT = "power2.out";

  inners.forEach(function (inner) {
    var title = inner.querySelector(".scroll-main-title");
    var subtitle = inner.querySelector(".scroll-subtitle");
    var cta = inner.querySelector(".scroll-cta-button");
    var captionEls = [subtitle, cta].filter(Boolean);
    var media = inner.querySelector(".smart-video-media");

    if (!title || !captionEls.length) return;

    if (reduced) {
      inner.classList.add("smart-video-reveal--no-gsap");
      gsap.set(title, { opacity: 1, y: 0 });
      gsap.set(captionEls, { opacity: 1, y: 0 });
      if (media) gsap.set(media, { y: 0 });
      return;
    }

    gsap.set(captionEls, { opacity: 0, y: "14vh" });
    if (media) gsap.set(media, { y: "6vh" });

    var tl = gsap.timeline({
      scrollTrigger: {
        trigger: inner,
        start: "top top",
        end: pinEndDistance,
        pin: inner,
        pinSpacing: false,
        scrub: 0.75,
        anticipatePin: 0.65,
        invalidateOnRefresh: true,
        fastScrollEnd: false,
      },
    });

    /*
     * Breakthrough Energy–style (same idea as .smart-video-slide):
     * 1) Headline scrubs UP into view (from below).
     * 2) Headline scrubs up/out while body + CTA scrub up/in; video drifts slightly.
     */
    var titleInDur = 0.3;
    var titleOutStart = 0.3;
    var titleOutDur = 0.46;
    var captionInStart = 0.42;
    var captionInDur = 0.52;
    var mediaStart = 0.3;

    tl.fromTo(
      title,
      { opacity: 0, y: "18vh" },
      { opacity: 1, y: 0, duration: titleInDur, ease: EASE_OUT },
      0
    );
    tl.to(
      title,
      { opacity: 0, y: "-12vh", duration: titleOutDur, ease: EASE_IN },
      titleOutStart
    );
    tl.fromTo(
      captionEls,
      { opacity: 0, y: "14vh" },
      { opacity: 1, y: 0, duration: captionInDur, ease: EASE_OUT },
      captionInStart
    );

    if (media) {
      tl.fromTo(
        media,
        { y: "6vh" },
        { y: "-10vh", duration: 0.88, ease: "none" },
        mediaStart
      );
    }
  });


  window.addEventListener(
    "load",
    function () {
      ScrollTrigger.refresh();
    },
    { once: true }
  );
})();
